import { RootState } from '@store/store'
import assert from 'assert'
import { ExtendedTuple } from 'types/helper'

// TODO: create this type at build time (JGAFAR)
export enum EnginePrimitive {
  BOX = 0,
  CYLINDER = 1,
  SPHERE = 2,
  CAPSULE = 3,
  HON = 4,
  DRO = 5,
  TORUS = 6,
  SHOEHORN = 7,
  TRIANGLE = 8,
  CONE = 9,
  STAR = 10,
  POLYGON = 11,
  EGG = 12,
  PIN = 13
}

// TODO: create this type at build time (JGAFAR)
export enum Engine2DPrimitive {
  RECTANGLE = 0
}

export enum EngineRepeatType {
  NONE = 0,
  XYZ = 1,
  ANGLE = 2
}

export enum EngineRepeatAngleDirection {
  X = 0,
  Y = 1,
  Z = 2
}

export enum PrimitiveDimensionType {
  EXTRUDE = 0,
  REVOLVE = 1
}

export enum PrimitiveSymmetryChecked {
  UNCHECKED = 0,
  CHECKED = 1
}

export enum MaterialType {
  P = 1,
  E = 2,
  PIXEL = 3,
  I = 4
}

export enum MaterialParam {
  ROUGHNESS = 0,
  METALNESS = 1,
  REFLECTIVE = 2,
  OPACITY = 3,
  E_SPECULAR_INTENSITY = 0,
  E_SPECULAR_SIZE = 1,
  I_STROKE_SIZE = 0,
  I_HIGHLIGHT_INTENSITY = 1,
  I_STROKE_INTENSITY = 2,
  I_COLORVAR_INTENSITY = 3,
  I_SCALE_X = 4,
  I_SCALE_Y = 5,
  I_ANGLE = 6
}

export enum MaterialColorParam {
  COLOR = 0,
  E_COLOR_TOP = 0,
  E_COLOR_LIG = 1,
  E_COLOR_SHA = 2,
  I_COLOR = 0,
  I_EMISSIVE_COLOR = 1
}

export type MaterialProperties = Record<
  keyof typeof MaterialColorParam | keyof typeof MaterialParam,
  number | string
>

export enum EngineBackground {
  SOLID = 0,
  GRADIENT_RADIAL = 1,
  GRADIENT_LINEAR = 2
}

export enum EngineCamera {
  PERSPECTIVE = 0,
  ISOMETRIC = 1,
  FREE_ORTHOGONAL = 2
}

export function isOrthographic(type: EngineCamera): boolean {
  let ok =
    type === EngineCamera.ISOMETRIC || type === EngineCamera.FREE_ORTHOGONAL
  return ok
}

export function toggleIsometricConstraint(
  cameraType: EngineCamera
): EngineCamera {
  return cameraType === EngineCamera.ISOMETRIC
    ? EngineCamera.FREE_ORTHOGONAL
    : EngineCamera.ISOMETRIC
}

export enum EngineCameraProperty {
  // Focal Length/Size depends on the camera type
  FOCAL_LENGTH_OR_SIZE = 0,
  FOCAL_PLANE = 1,
  APERTURE = 2,
  DISTORTION = 3
}

export enum EngineUndoRedo {
  UNDO = 0,
  REDO = 1
}

export enum EngineBlendType {
  ADD = 0,
  SUBTRACT = 1,
  INTERSECT = 2,
  COLOR = 3,
  REPEL = 4,
  AVOID = 5
}

export enum EngineSelectParentChild {
  UP = -1,
  DOWN = 1
}

export enum EngineSelectSibling {
  LEFT = -1,
  RIGHT = 1
}

export enum EngineSymmetryAxis {
  X = 0
}

export enum EngineLightStatus {
  ENABLED = 0,
  DISABLED = 1
}

export enum EngineMode {
  NORMAL = 1,
  OUTLINE = 2,
  PIXEL = 3,
  ILLUSTRATIVE = 4
}

export enum EngineSelectedSceneNode {
  NONE = 0,
  SHAPE = 1,
  GROUP = 2,
  PROJECT = 3,
  MULTI_SELECTED = 4
}

export type EngineData =
  | {
      status: EngineSelectedSceneNode.NONE
      sceneData: string
    }
  | {
      status: EngineSelectedSceneNode.SHAPE
      sceneData: string | SceneElement
    }
  | {
      status: EngineSelectedSceneNode.GROUP
      sceneData: SceneElementGroup
    }
  | {
      status: EngineSelectedSceneNode.PROJECT
      origin: EngineDataOrigin
      sceneData: Scene
    }
  | {
      status: EngineSelectedSceneNode.MULTI_SELECTED
      sceneData: string
    }

export interface SelectedObjectUI {
  status: number
  editButtonGizmoPosition: {
    centerX: number
    centerY: number
  }
}

export enum EngineCommitChange {
  // Looks weird set these to these values, but the lights uses the old undo/redo api, and
  // the old api treats begin commit as a 1 and an end commit as a 4
  BEGIN_COMMIT = 1,
  END_COMMIT = 4
}

export enum EngineCommitOrigin {
  LOCAL = 0,
  REMOTE = 2
}

export enum EngineDataOrigin {
  LOCAL_INPUT = 0,
  ENGINE_OUTPUT = 1,
  REMOTE_INPUT = 2
}

export type FrameType = number

export interface Scene {
  info: SceneInfo
  background: ExtendedTuple<number, 7>
  camera: [EngineCamera, number, number, number, number] // [cameraType, cameraValue, cameraFocalPlane, cameraAperture, cameraDistortion]
  floor: { enabled: boolean; height: number }
  frame: [boolean, number, FrameType, number, number] // [enabled, opacity, frame type, dimensionX, dimensionY]
  style:
    | { id: EngineMode.NORMAL }
    | {
        id: EngineMode.OUTLINE
        color: [number, number, number]
        outline: number
      }
    | {
        id: EngineMode.PIXEL
        color: [number, number, number]
        pixelsize: number
        filter: number
        outline: boolean
      }
  // | {
  //     id: EngineMode.ILLUSTRATIVE
  //   }

  lighting: {
    occlusion: { distance: number; color: [number, number, number] }
    lights: [{ position: number; altitude: number }]
  }
}

export interface SceneInfo {
  name: string
  size: number
}

export interface SceneElement {
  pos: ExtendedTuple<number, 3>
  rot: ExtendedTuple<number, 3>
  sca: ExtendedTuple<number, 3>
  symmetry: ExtendedTuple<boolean, 3>
  repeat:
    | [EngineRepeatType.NONE]
    | [EngineRepeatType.XYZ, number, number, number, number, number, number]
    | [EngineRepeatType.ANGLE, number, number, number]

  group: SceneGroup
  address: string
  blend: [EngineBlendType, number]
  material:
    | [
        number,
        MaterialType.P,
        number,
        number,
        number,
        number,
        number,
        number,
        number,
        number
      ]
    | [
        number,
        MaterialType.E,
        number,
        number,
        number,
        number,
        number,
        number,
        number,
        number,
        number,
        number,
        number,
        number,
        number
      ]
    | [
        number,
        MaterialType.PIXEL,
        number,
        number,
        number,
        number,
        number,
        number,
        number,
        number,
        number,
        number,
        number,
        number,
        number
      ]
    | [
        number,
        MaterialType.I,
        number,
        number,
        number,
        number,
        number,
        number,
        number,
        number,
        number,
        number,
        number,
        number,
        number,
        number
      ]
  prim: ScenePrim
}

export interface SceneElementGroup
  extends Omit<SceneElement, 'material' | 'prim'> {}

export interface SceneGroup {
  expanded: boolean
}

export enum ScenePrimType {
  SOFT_BOX = 0,
  STAR = 1,
  HORSESHOE = 2,
  TRIANGLE = 3,
  EGG = 4,
  TEXT = 5,
  NUM = 6
}

export type SceneParamGeneric<
  Type extends ScenePrimType,
  ParamLength extends number,
  DimensionType extends PrimitiveDimensionType
> = {
  type: Type
  params: ExtendedTuple<number, ParamLength>
  common: ExtendedTuple<number, 3>
  dimension: ExtendedTuple<number, 4>
  thickness: ValueAndRange
  round: ValueAndRange
} & (DimensionType extends PrimitiveDimensionType.EXTRUDE
  ? {
      corner1: ValueAndRange
      corner2: ValueAndRange
    }
  : {}) &
  (Type extends ScenePrimType.STAR ? { cornerStar: ValueAndRange } : {})

export type ScenePrim = ScenePrimByDimensionType<PrimitiveDimensionType>

export type ScenePrimByDimensionType<
  DimensionType extends PrimitiveDimensionType
> =
  | (SceneParamGeneric<ScenePrimType.SOFT_BOX, 4, DimensionType> & {
      settings: { scaleCorners: boolean }
    })
  | SceneParamGeneric<ScenePrimType.STAR, 3, DimensionType>
  | SceneParamGeneric<ScenePrimType.HORSESHOE, 6, DimensionType>
  | SceneParamGeneric<ScenePrimType.TRIANGLE, 1, DimensionType>
  | SceneParamGeneric<ScenePrimType.EGG, 4, DimensionType>
  | SceneParamGeneric<ScenePrimType.NUM, 5, DimensionType>

export enum GroupExpanded {
  NORMAL = 0,
  EXPANDED = 1
}

export interface LocalUserLocation {
  location: ExtendedTuple<number, 8>
}

export interface RemoteUserStatus {
  remote_user_uuid: string
  status: string
  color?: ExtendedTuple<number, 3>
}

export interface RemoteUserLocation {
  remote_user_uuid: string
  location: ExtendedTuple<number, 8>
}

export interface SceneNavigatorData {
  info: {
    elements: SceneNavigatorElement[]
    name: string
    depth: number
  }
}

export enum ElementIconId {
  GROUP = 0,
  CUBE = 1,
  CYLINDER = 2,
  SPHERE = 3,
  CAPSULE = 4,
  CONE = 5,
  TORUS = 6,
  U = 7,
  DROP = 8,
  TRIANGLE = 9,
  CONE_DUPE = 10,
  STAR = 11,
  POLYGON = 12,
  EGG = 13,
  PIN = 14,
  TEXT = 15,
  CURVES = 16
}

export interface SceneNavigatorElement {
  blend: number
  color: number
  combine: EngineBlendType
  iconID: ElementIconId
  locked: boolean
  selected: boolean
  uuid: string
  visible: boolean
  name: string
  hovered: boolean
}

export type SceneNavigatorEventPayload =
  | {
      data: SceneNavigatorData
      status: 'AT_LEAST_ONE_ELEMENT_SELECTED'
    }
  | {
      status: 'NO_ELEMENT_SELECTED'
    }

export interface Cartesian {
  x: number
  y: number
  z: number
}

export class CCartesian implements Cartesian {
  x = 0
  y = 0
  z = 0

  constructor()
  constructor(x: number, y: number, z: number)
  constructor(...params: any[]) {
    if (params.length == 0) {
      // do nothing already initialized in declaration
    } else if (params.length == 3) {
      this.x = params[0]
      this.y = params[1]
      this.z = params[3]
    } else {
      assert('invalid args')
    }
  }
}
export interface Spherical {
  r: number
  phi: number
  theta: number
}

export class CSpherical implements Spherical {
  r = 0
  phi = 0
  theta = 0
}
export interface CameraPosition {
  target: Cartesian
  view: Spherical
}

export class CCameraPosition implements CameraPosition {
  target = new CCartesian()
  view = new CSpherical()
}

export interface CameraFromTo {
  from: Cartesian
  to: Cartesian
}

export enum EngineStackReorderingMode {
  BEFORE = 0,
  AFTER = 1
}

export interface EngineStackReorderingOpts {
  srcIndices: Array<number>
  destIndex: number
  mode: EngineStackReorderingMode
}

export type EngineCapturePayload = {
  objectURL: string
  blobArray?: Uint8Array
  depthObjectURL?: string
  depthBlobArray?: Uint8Array
  colorObjectURL?: string
  colorBlobArray?: Uint8Array
  format: 'jpg' | 'png' | 'svg' | 'screenshot' | 'reference' | 'obj'
}

export type EngineExportCapturePayload =
  | {
      format: 'svg'
      splitFaceEnabled: boolean
    }
  | {
      format: 'png'
      transparentBackground: boolean
      compressionLevel?: number // between 0 and 9
    }
  | {
      format: 'jpg'
      quality?: number // between 0 and 100
    }
  | {
      format: 'screenshot'
    }
  | {
      format: 'reference'
    }
  | {
      format: 'obj'
    }

export type FrameCallbackData = {
  info: {
    enabled: boolean
    size: [number, number]
    topleft: [number, number]
  }
}

export type FrameEventPayload = {
  enabled: boolean
  size: { height: number; width: number }
  position: { top: number; left: number }
}

export enum DeviceStatusEventPayload {
  SLOW_INTEL_GPU = 'SLOW_INTEL_GPU'
}

export type ModelLoadedStatusEventPayload = {
  status?: number
  sceneData?: string
}

export type RequestFontPayload = {
  fontId: string
}

export interface ValueAndRange {
  value: number
  min: number
  max: number
}

export type Vec2d = { x: number; y: number }
export type IVec2 = Vec2d
export type Vec3d = { x: number; y: number; z: number }
export type IVec3 = Vec3d

export function isVec2(value: any): value is IVec2 | Vec2d {
  return (
    typeof value.x === 'number' &&
    typeof value.y === 'number' &&
    typeof value.z === 'undefined'
  )
}

export function isVec3(value: any): value is IVec3 | Vec3d {
  return (
    typeof value.x === 'number' &&
    typeof value.y === 'number' &&
    typeof value.z === 'number'
  )
}

export type PropertyPanelId = string

export type PropertyPathVariant =
  | 'int'
  | 'float'
  | 'double'
  | 'vec2d'
  | 'vec3d'
  | 'bool'
  | 'EnumBase'
  | 'string'
  | 'ivec2'
  | 'ivec3'

export const IntGuard = (val: any): boolean => {
  const numVal = Number(val)
  return (
    typeof numVal === 'number' && !isNaN(numVal) && Number.isInteger(numVal)
  )
}

export const FloatGuard = (val: any): boolean => {
  const numVal = Number(val)
  return typeof numVal === 'number' && !isNaN(numVal)
}

export const Vec2dGuard = (val: any): boolean => {
  return VecGuard(val, 2)
}

export const Vec3dGuard = (val: any): boolean => {
  return VecGuard(val, 3)
}

const VecGuard = (val: any, vecLength: number) => {
  const keys = Object.keys(val)

  return (
    Array.isArray(keys) &&
    keys.length === vecLength &&
    keys.every(key => typeof val[key] === 'number')
  )
}

export const BoolGuard = (val: any): boolean => {
  return typeof val === 'boolean'
}

export const EnumBaseGuard = IntGuard

export const StringGuard = (val: any): boolean => {
  return typeof val === 'string'
}

export const IVec2Guard = (val: any): boolean => {
  return IntGuard(val.x) && IntGuard(val.y) && Vec2dGuard(val)
}

export const IVec3Guard = (val: any): boolean => {
  return (
    IntGuard(val.x) && IntGuard(val.y) && IntGuard(val.z) && Vec3dGuard(val)
  )
}

export type PropertyPanelSetter = (
  path: string,
  value: any,
  panelId?: string
) => PropertyPanelSetStatus

export enum PropertyPanelSetStatus {
  WrongPath,
  IncorrectType,
  ValueAlreadySet,
  IncorrectValue,
  Success
}

export const propertyPaths = [
  'shapeMaterial.color',
  'shapeMaterial.pbrColor',
  'shapeMaterial.pbrRoughness',
  'shapeMaterial.pbrMetalness',
  'shapeMaterial.opacity',
  'shapeMaterial.pbrIsReflective',
  'treeNode.location.position',
  'treeNode.location.rotation',
  'treeNode.standard.name',
  'treeNode.standard.blendMode',
  'treeNode.standard.blendShape',
  'treeNode.standard.repeatMode',
  'treeNode.standard.repeatX',
  'treeNode.standard.repeatY',
  'treeNode.standard.repeatZ',
  'treeNode.standard.distanceX',
  'treeNode.standard.distanceY',
  'treeNode.standard.distanceZ',
  'treeNode.standard.circularCopyAxis',
  'treeNode.standard.circularCopyCount',
  'treeNode.standard.circularCopyDistance',
  'treeNode.standard.alongX',
  'treeNode.standard.alongY',
  'treeNode.standard.alongZ',
  'treeNode.standard.onion',
  'treeNode.standard.round',
  'treeNode.standard.thickness',
  'treeNode.dimension.mode',
  'treeNode.dimension.extrudeAmount',
  'treeNode.dimension.topFillet',
  'treeNode.dimension.bottomFillet',
  'treeNode.dimension.distance',
  'treeNode.dimension.rotation',
  'treeNode.profile.width',
  'treeNode.profile.height',
  'treeNode.profile.cornerRounding0',
  'treeNode.profile.cornerRounding1',
  'treeNode.profile.cornerRounding2',
  'treeNode.profile.cornerRounding3',
  'treeNode.drawMaterial',
  'treeNode.profile.textBoxSize',
  'treeNode.profile.angle',
  'treeNode.profile.radius',
  'treeNode.profile.length',
  'treeNode.profile.thickness',
  'treeNode.profile.corners',
  'treeNode.profile.dartOffset',
  'treeNode.profile.sideCount',
  'treeNode.profile.roundingRadius',
  'treeNode.profile.radiusA',
  'treeNode.profile.radiusB',
  'treeNode.profile.font',
  'treeNode.profile.text',
  'treeNode.profile.size',
  'treeNode.profile.letterSpacing',
  'treeNode.profile.lineHeight',
  'treeNode.profile.leadingType',
  'shapeMaterial.exprMaterialMode',
  'shapeMaterial.exprColorTop',
  'shapeMaterial.exprColorLight',
  'shapeMaterial.exprColorDark',
  'shapeMaterial.exprSpecularSize',
  'shapeMaterial.exprSpecularIntensity',
  'shapeMaterial.illustrativeMaterialMode',
  'shapeMaterial.illustrativeColor',
  'shapeMaterial.illustrativeEmissiveColor',
  'shapeMaterial.illustrativeStrokeSize',
  'shapeMaterial.illustrativeSpecularIntensity',
  'shapeMaterial.illustrativeNormalIntensity',
  'shapeMaterial.illustrativeColorVariation',
  'shapeMaterial.illustrativeScaleX',
  'shapeMaterial.illustrativeScaleY',
  'shapeMaterial.illustrativeAngle',
  'multiSelectedShapes.standard.visible',
  'cameraLens.distortionAmount',
  'cameraLens.dofAperture',
  'cameraLens.dofFocalPlane',
  'cameraLens.perspFocalLength',
  'cameraLens.orthoSize',
  'background.colorA',
  'background.colorB',
  'background.gradientMode',
  'frame.frameType',
  'frame.size',
  'frame.opacity',
  'frame.enabled',
  'floor.enabled',
  'floor.height',
  'cameraLens.type',
  'style.style',
  'style.exprOutline',
  'style.exprColor',
  'style.pixelArtPixelCount',
  'style.pixelArtEnabled',
  'style.pixelArtColor',
  'style.pixelArtOutlineThickness',
  'style.illusLightTexture',
  'style.illusShadowTexture',
  'style.illusGlobalStrokeSize',
  'style.illusFilterStrength',
  'style.illusEdgeStrength',
  'style.illusLightIntensity',
  'style.illusAOIntensity',
  'style.illusOutlineEnabled',
  'style.illusOutlineTolerance',
  'style.illusOutlineColor',
  'style.illusHighlightColor',
  'style.illusShadowColor',
  'style.illusSkyColor',
  'style.illusBounceColor',
  'lightOcclusion.distance',
  'directionalLight.direction',
  'treeNode.standard.visible',
  'cameraLens.perspSensorSize',
  'directionalLight.index',
  'directionalLight.enabled',
  'directionalLight.color',
  'directionalLight.shadowEnable',
  'directionalLight.shadowBlur',
  'directionalLight.diffIntensity',
  'directionalLight.diffBias',
  'lightOcclusion.enabled',
  'lightOcclusion.color',
  'treeNode.standard.locked',
  'treeNode.profile.bulge'
] as const

export type PropertyPanelPath = (typeof propertyPaths)[number]

export type PropertyHasChangedData = {
  path: PropertyPanelPath
  panelId: string
  value: any
}

export type EnginePropertyListType = {
  Label:
    | 'Frame'
    | 'Background'
    | 'Floor'
    | 'Camera lens'
    | 'Shape material'
    | 'Primitive parameters'
    | 'Light occlusion'
    | 'Directional light'
  Properties: {
    path: PropertyPanelPath
    type: PropertyPathVariant
    visibility: boolean
    value: any
    softMin?: any
    softMax?: any
    hardMin?: any
    hardMax?: any
  }[]
}

export const boundsTypes = ['softMin', 'softMax', 'hardMin', 'hardMax'] as const
export type BoundsType = (typeof boundsTypes)[number]
export type BoundsVariant = number | IVec2 | IVec3
export type PropertyList = Partial<
  Record<
    PropertyPanelPath,
    {
      type: string
      value: any
      softMin?: BoundsVariant
      softMax?: BoundsVariant
      hardMin?: BoundsVariant
      hardMax?: BoundsVariant
    }
  >
>

export type PropertyListHasChangedData = {
  UI: {
    Groups: EnginePropertyListType[]
  }
}

export type PropertySoftBoundsHaveChangedData = {
  path: PropertyPanelPath
  min: number
  max: number
}

export type SceneContentData = {
  primitiveType: RootState['scene']['primitiveType']
  selectedSceneNode: RootState['scene']['selectedSceneNode']
  styleMode: EngineMode
}

export type SnapshotListHasChangedData = { snapshots: Snapshot[] }
export type SnapshotRenamedData = { snapshot: [Snapshot] }
export type Snapshot = { index: number; name: string }
export const VariableColorModeValues = ['Shaded', 'Split', 'Flat'] as const
export type VariableColorModeType = (typeof VariableColorModeValues)[number]

export type SceneLayer = '2D' | '3D'
